/**
 * Vectors API Hook
 */

import { useApiData, useAuthenticatedFetch } from './client';
import type { Vector } from './types';

export function useVectors(options?: {
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
}) {
  // NOTE: api-edna accepts `limit`, `order_by`, and `ascending` only. It has no
  // `skip`/`offset`/cursor, and no eager-loading flags — collections are capped at
  // 1000 with truncation signalled in the `X-Result-Truncated` response header.
  // Options it ignores were removed rather than left as silent no-ops.
  const params = new URLSearchParams();
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.orderBy) params.set('order_by', options.orderBy);
  if (options?.ascending !== undefined) params.set('ascending', String(options.ascending));

  const queryString = params.toString();
  const endpoint = queryString ? `/vectors?${queryString}` : '/vectors';

  return useApiData<Vector[]>(endpoint);
}

export function useVectorMutations() {
  const { fetchData } = useAuthenticatedFetch<Vector>();

  const createVector = (data: {
    id_project: number;
    active: boolean;
    origin: string;
    embedding_provider: string;
    embedding: string;
    length: number;
    model_labels?: string;
    id_model?: number;
  }) =>
    fetchData('/vectors', {
      method: 'POST',
      body: JSON.stringify(data),
    });

  const updateVector = (id: number, data: Partial<Vector>) =>
    fetchData(`/vectors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

  const deleteVector = (id: number) =>
    fetchData(`/vectors/${id}`, { method: 'DELETE' });

  return { createVector, updateVector, deleteVector };
}
